import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { examApi, classesApi, subjectsApi } from '../../services/api';
import { toast } from 'sonner';
import {
  BookMarked, Save, Upload, Loader2, FileText,
} from 'lucide-react';

const BOARDS = ['CBSE', 'ICSE', 'State Board', 'IB', 'IGCSE', 'Other'];

const SyllabusPage = () => {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [syllabusList, setSyllabusList] = useState([]);
  const [classId, setClassId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [board, setBoard] = useState('CBSE');
  const [chaptersText, setChaptersText] = useState('');  // newline-separated
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);

  useEffect(() => {
    (async () => {
      const [c, s, syl] = await Promise.all([
        classesApi.getAll(),
        subjectsApi.getAll(),
        examApi.listSyllabus(),
      ]);
      setClasses(c.data.classes || []);
      setSubjects(s.data.subjects || []);
      setSyllabusList(syl.data.syllabus || []);
    })();
  }, []);

  const filteredSubjects = useMemo(() => {
    if (!classId) return subjects;
    return subjects.filter(s => s.class_id === classId);
  }, [subjects, classId]);

  // Load saved chapters when class+subject selected
  useEffect(() => {
    if (!classId || !subjectId) return;
    const existing = syllabusList.find(
      s => s.class_id === classId && s.subject_id === subjectId,
    );
    if (existing) {
      setBoard(existing.board || 'CBSE');
      setChaptersText((existing.chapters || []).join('\n'));
    } else {
      setChaptersText('');
    }
  }, [classId, subjectId, syllabusList]);

  const save = async () => {
    if (!classId || !subjectId) { toast.error('Choose class & subject'); return; }
    const chapters = chaptersText.split('\n').map(s => s.trim()).filter(Boolean);
    if (chapters.length === 0) { toast.error('Add at least one chapter'); return; }
    setSaving(true);
    try {
      await examApi.upsertSyllabus({
        class_id: classId, subject_id: subjectId, board, chapters,
      });
      toast.success(`Saved ${chapters.length} chapter${chapters.length === 1 ? '' : 's'}`);
      const { data } = await examApi.listSyllabus();
      setSyllabusList(data.syllabus || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    try {
      const { data } = await examApi.extractSyllabusPdf(file);
      // Append extracted text as a hint at the end
      setChaptersText(prev =>
        (prev ? prev + '\n\n--- Extracted from PDF (review & edit) ---\n' : '') +
        data.text);
      toast.success(`Extracted ${data.chars} chars from PDF`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PDF extract failed');
    } finally {
      setExtracting(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-6" data-testid="syllabus-page">
      <div>
        <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
          <BookMarked className="h-6 w-6 text-primary" /> Syllabus / Chapter List
        </h1>
        <p className="text-sm text-muted-foreground">
          Define the chapters for each class + subject. AI uses this to generate on-syllabus questions.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle>Edit Chapters</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Class *</Label>
            <Select value={classId} onValueChange={(v) => { setClassId(v); setSubjectId(''); }}>
              <SelectTrigger data-testid="syl-class"><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent>
                {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Subject *</Label>
            <Select value={subjectId} onValueChange={setSubjectId}>
              <SelectTrigger data-testid="syl-subject"><SelectValue placeholder="Select subject" /></SelectTrigger>
              <SelectContent>
                {filteredSubjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Board</Label>
            <Select value={board} onValueChange={setBoard}>
              <SelectTrigger data-testid="syl-board"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BOARDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label>Chapters / Topics (one per line)</Label>
              <div>
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-primary hover:underline">
                  {extracting
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Upload className="h-4 w-4" />}
                  {extracting ? 'Extracting…' : 'Upload syllabus PDF'}
                  <input type="file" accept="application/pdf" className="hidden"
                         onChange={handlePdfUpload} disabled={extracting}
                         data-testid="syl-pdf-upload" />
                </label>
              </div>
            </div>
            <Textarea rows={10} value={chaptersText}
                      placeholder={"Chapter 1: Number System\nChapter 2: Operations on Numbers\nChapter 3: Fractions and Decimals\n..."}
                      onChange={e => setChaptersText(e.target.value)}
                      data-testid="syl-chapters" />
            <p className="text-xs text-muted-foreground">
              Each non-empty line is treated as one chapter/topic. Used by the AI Question Setter.
            </p>
          </div>
          <div className="md:col-span-3 flex justify-between items-center">
            {classId && filteredSubjects.length === 0 && (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                No subjects mapped to this class. Add subjects in <b>Subjects</b> first.
              </p>
            )}
            <Button onClick={save} disabled={saving || !classId || !subjectId} data-testid="syl-save" className="ml-auto">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Chapters
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" /> Saved Syllabus Entries
          </CardTitle>
        </CardHeader>
        <CardContent>
          {syllabusList.length === 0 ? (
            <p className="text-sm text-muted-foreground">No syllabus configured yet.</p>
          ) : (
            <div className="space-y-2">
              {syllabusList.map(s => {
                const c = classes.find(x => x.id === s.class_id);
                const sb = subjects.find(x => x.id === s.subject_id);
                return (
                  <div key={s.id} className="border rounded p-3 flex items-start justify-between gap-3"
                       data-testid={`syl-row-${s.id}`}>
                    <div className="flex-1">
                      <p className="font-medium">
                        {c?.name || s.class_id} · {sb?.name || s.subject_id}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {(s.chapters || []).slice(0, 6).join(' · ')}
                        {s.chapters?.length > 6 && ` … +${s.chapters.length - 6} more`}
                      </p>
                    </div>
                    <div className="text-right space-y-1">
                      <Badge variant="outline">{s.board || 'CBSE'}</Badge>
                      <p className="text-xs text-muted-foreground">{s.chapters?.length || 0} chapters</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SyllabusPage;
